import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { exportToCSV, exportToXLSX, exportToXLS, exportToPDF, exportNeftRtgsXlsx } from '../utils/reportExport';
import { API_URL } from '../config';
import './AdminPointsReportPage.css';

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminPointsReportPage = () => {
  const [users, setUsers] = useState([]);
  const [totals, setTotals] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [levelConfig, setLevelConfig] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('totalEarned');
  const [sortOrder, setSortOrder] = useState('desc');
  const [payoutStatus, setPayoutStatus] = useState('Pending');
  const [payoutRows, setPayoutRows] = useState([]);
  const [payoutTotals, setPayoutTotals] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [selectedPayoutIds, setSelectedPayoutIds] = useState([]);
  const [debitAccount, setDebitAccount] = useState('');
  const [companyName, setCompanyName] = useState('Yelooo');
  const [paymentReference, setPaymentReference] = useState('');
  const [payoutMessage, setPayoutMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchLevelConfig();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchPayouts();
  }, [payoutStatus]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/Points/admin/all-users?page=${currentPage}&pageSize=15&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const response = await axios.get(url, {
        headers: getAuthHeader()
      });
      const rawUsers = response.data.users;
      setUsers(Array.isArray(rawUsers) ? rawUsers : (rawUsers?.$values || []));
      setTotals(response.data.totals);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevelConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/Points/level-config`);
      const data = response.data;
      // Handle $values wrapper
      setLevelConfig({
        ...data,
        levels: Array.isArray(data.levels) ? data.levels : (data.levels?.$values || [])
      });
    } catch (error) {
      console.error('Error fetching level config:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      setPayoutLoading(true);
      const response = await axios.get(`${API_URL}/Points/admin/payouts?status=${encodeURIComponent(payoutStatus)}`, {
        headers: getAuthHeader()
      });
      const rows = Array.isArray(response.data?.rows) ? response.data.rows : (response.data?.rows?.$values || []);
      setPayoutRows(rows);
      setPayoutTotals(response.data?.totals || null);
      if (response.data?.debitAccountNumber && !debitAccount) {
        setDebitAccount(response.data.debitAccountNumber);
      }
      if (response.data?.companyName) {
        setCompanyName(response.data.companyName);
      }
      setSelectedPayoutIds([]);
    } catch (error) {
      console.error('Error fetching point payouts:', error);
      setPayoutRows([]);
    } finally {
      setPayoutLoading(false);
    }
  };

  const normalizeArray = (arr) => Array.isArray(arr) ? arr : (arr?.$values || []);

  const fetchUserDetail = async (userId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API_URL}/Points/admin/user/${userId}`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      setUserDetail({
        ...data,
        levelEarnings: normalizeArray(data?.levelEarnings),
        recentTransactions: normalizeArray(data?.recentTransactions)
      });
    } catch (error) {
      console.error('Error fetching user detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserDetail(user.userId);
  };

  const closeDetail = () => {
    setSelectedUser(null);
    setUserDetail(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleExport = async (format) => {
    let data = users;
    if (users.length < (pagination?.totalCount ?? 0)) {
      try {
        const res = await axios.get(
          `${API_URL}/Points/admin/all-users?page=1&pageSize=10000&sortBy=${sortBy}&sortOrder=${sortOrder}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`,
          { headers: getAuthHeader() }
        );
        data = Array.isArray(res.data.users) ? res.data.users : (res.data.users?.$values || []);
      } catch (err) {
        console.error('Export fetch error:', err);
        data = users;
      }
    }
    const exportData = data.map(u => ({
      CustomerCode: u.customerCode || '',
      Username: u.username || '',
      Email: u.email || '',
      AadharNumber: u.aadharNumber || '',
      PanNumber: u.panNumber || '',
      GstNumber: u.gstNumber || '',
      TotalEarned: u.balance?.totalEarned?.toFixed(2) || '0.00',
      SelfEarned: u.selfEarnings?.toFixed(2) || '0.00',
      ReferralEarned: u.referralEarnings?.toFixed(2) || '0.00',
      CurrentBalance: u.balance?.currentBalance?.toFixed(2) || '0.00',
    }));
    const name = `customer-points-report-${new Date().toISOString().slice(0, 10)}`;
    if (format === 'csv') exportToCSV(exportData, name);
    else if (format === 'xlsx') exportToXLSX(exportData, name, 'Points Report');
    else if (format === 'xls') exportToXLS(exportData, name, 'Points Report');
    else if (format === 'pdf') exportToPDF(exportData, name, 'Customer Points Report');
  };

  const readyNeftRows = payoutRows.filter((row) => row.hasBank);
  const togglePayoutRow = (userId) => {
    setSelectedPayoutIds((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };
  const toggleAllReadyPayouts = () => {
    const ids = readyNeftRows.map((row) => row.userId);
    const allSelected = ids.length > 0 && ids.every((id) => selectedPayoutIds.includes(id));
    setSelectedPayoutIds(allSelected ? [] : ids);
  };

  const handleDownloadNeft = () => {
    if (!debitAccount.trim()) {
      setPayoutMessage({ type: 'error', text: 'Enter the company Debit A/C Number used for NEFT/RTGS.' });
      return;
    }
    if (readyNeftRows.length === 0) {
      setPayoutMessage({ type: 'error', text: 'No unpaid customers with bank details to include in the NEFT file.' });
      return;
    }
    const batchLabel = paymentReference.trim() || `CH NO ${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
    exportNeftRtgsXlsx({
      companyName,
      batchLabel,
      debitAccount: debitAccount.trim(),
      rows: readyNeftRows.map((row) => ({
        amount: row.amountRupees,
        ifscCode: row.ifscCode,
        accountNumber: row.accountNumber,
        beneficiaryName: row.beneficiaryName,
        accountType: row.accountType,
        customerCode: row.customerCode,
        aadharNumber: row.aadharNumber,
        panNumber: row.panNumber,
        gstNumber: row.gstNumber,
      })),
      filename: `NEFT-RTGS-customer-points-${new Date().toISOString().slice(0, 10)}`,
    });
    setPayoutMessage({ type: 'success', text: `Downloaded NEFT file for ${readyNeftRows.length} customer(s). 1 point = ₹1.` });
  };

  const handleMarkPayoutsPaid = async () => {
    if (selectedPayoutIds.length === 0) {
      setPayoutMessage({ type: 'error', text: 'Select at least one customer to mark as paid.' });
      return;
    }
    if (!window.confirm(`Pay and deduct points for ${selectedPayoutIds.length} customer(s)? Their current balance will be reduced by the unpaid amount (1 point = ₹1).`)) {
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/Points/admin/payouts/mark-paid`,
        {
          userIds: selectedPayoutIds,
          paymentReference: paymentReference.trim() || undefined,
          notes: 'NEFT/RTGS customer points payout',
        },
        { headers: getAuthHeader() }
      );
      setPayoutMessage({ type: 'success', text: response.data?.message || `Paid ₹${Number(response.data?.amountRupees || 0).toFixed(2)}. Customer points deducted.` });
      setSelectedPayoutIds([]);
      fetchPayouts();
      fetchUsers();
    } catch (err) {
      const data = err.response?.data;
      setPayoutMessage({ type: 'error', text: data?.message || data?.title || 'Failed to update payment status.' });
    }
  };

  return (
    <div className="admin-points-page">
      <Header />
      <div className="admin-points-container">
        <div className="page-header-row">
          <h1 className="page-title">Points Report</h1>
          <div className="export-buttons">
            <span className="export-label">Download:</span>
            <button type="button" className="export-btn" onClick={() => handleExport('csv')}>CSV</button>
            <button type="button" className="export-btn" onClick={() => handleExport('xlsx')}>XLSX</button>
            <button type="button" className="export-btn" onClick={() => handleExport('xls')}>XLS</button>
            <button type="button" className="export-btn" onClick={() => handleExport('pdf')}>PDF</button>
            <button type="button" className="export-btn export-btn-neft" onClick={handleDownloadNeft}>NEFT Excel (BoB)</button>
          </div>
        </div>

        {totals && (
          <div className="totals-summary">
            <div className="total-card">
              <span className="total-label">Total Points Distributed</span>
              <span className="total-value">{totals.totalPointsDistributed?.toFixed(2)}</span>
            </div>
            <div className="total-card">
              <span className="total-label">Total Points Redeemed</span>
              <span className="total-value">{totals.totalPointsRedeemed?.toFixed(2)}</span>
            </div>
            <div className="total-card">
              <span className="total-label">Total Current Balance</span>
              <span className="total-value">{totals.totalCurrentBalance?.toFixed(2)}</span>
            </div>
            <div className="total-card">
              <span className="total-label">Total Users</span>
              <span className="total-value">{totals.totalUsers}</span>
            </div>
          </div>
        )}

        <div className="payout-section">
          <div className="payout-header">
            <div>
              <h2>Customer points payout (NEFT / RTGS)</h2>
              <p>1 point = ₹1. Download the Bank of Baroda NEFT file for the unpaid balance, then mark Paid. That deducts those points from the customer’s current balance.</p>
            </div>
            <div className="payout-status-tabs">
              <button type="button" className={payoutStatus === 'Pending' ? 'active' : ''} onClick={() => setPayoutStatus('Pending')}>Pending</button>
              <button type="button" className={payoutStatus === 'Paid' ? 'active' : ''} onClick={() => setPayoutStatus('Paid')}>Paid</button>
            </div>
          </div>

          {payoutTotals && (
            <div className="payout-summary">
              <span>{payoutTotals.customers || 0} customer(s)</span>
              <span>Ready for NEFT: {payoutTotals.readyForNeft || 0}</span>
              <span>Missing bank: {payoutTotals.missingBank || 0}</span>
              <span>Amount: ₹{(payoutTotals.amountRupees || 0).toFixed(2)}</span>
            </div>
          )}

          <div className="payout-controls">
            <label>
              Debit A/C Number
              <input
                type="text"
                value={debitAccount}
                onChange={(e) => setDebitAccount(e.target.value)}
                placeholder="Company account used for NEFT"
              />
            </label>
            <label>
              Cheque / batch ref
              <input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="e.g. CH NO 000003"
              />
            </label>
            <button type="button" className="export-btn export-btn-neft" onClick={handleDownloadNeft} disabled={payoutStatus !== 'Pending'}>
              Download NEFT Excel
            </button>
            {payoutStatus === 'Pending' && (
              <button type="button" className="view-btn" onClick={handleMarkPayoutsPaid}>
                Mark selected as Paid
              </button>
            )}
          </div>

          {payoutMessage.text && (
            <div className={`payout-message ${payoutMessage.type}`}>{payoutMessage.text}</div>
          )}

          <div className="users-table-container payout-table-wrap">
            {payoutLoading ? (
              <div className="loading">Loading payouts...</div>
            ) : payoutRows.length === 0 ? (
              <p className="empty-payout">No {payoutStatus.toLowerCase()} customer point payouts.</p>
            ) : (
              <table className="users-table payout-table">
                <thead>
                  <tr>
                    {payoutStatus === 'Pending' && (
                      <th>
                        <input
                          type="checkbox"
                          checked={readyNeftRows.length > 0 && readyNeftRows.every((row) => selectedPayoutIds.includes(row.userId))}
                          onChange={toggleAllReadyPayouts}
                          aria-label="Select all customers with bank details"
                        />
                      </th>
                    )}
                    <th>Customer</th>
                    <th>Beneficiary</th>
                    <th>Bank</th>
                    <th>Points / ₹</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutRows.map((row) => (
                    <tr key={row.userId} className={row.hasBank ? '' : 'missing-bank'}>
                      {payoutStatus === 'Pending' && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPayoutIds.includes(row.userId)}
                            onChange={() => togglePayoutRow(row.userId)}
                            aria-label={`Select ${row.username}`}
                          />
                        </td>
                      )}
                      <td>
                        <div className="user-cell">
                          <span className="user-name">{row.username}</span>
                          <span className="user-email">{row.email}</span>
                        </div>
                      </td>
                      <td>{row.beneficiaryName || '—'}</td>
                      <td>
                        {row.hasBank || row.hasAccount ? (
                          <>
                            <div>{row.bankName} · {row.accountType}</div>
                            <div className="user-email">{row.accountNumber} · {row.ifscCode || 'IFSC missing'}</div>
                            {row.bankFromLinkedAccount && (
                              <span className="bank-unverified">Using bank from linked account (same email)</span>
                            )}
                            {row.hasBank && !row.isBankVerified && <span className="bank-unverified">Bank KYC not verified</span>}
                            {!row.hasBank && <span className="bank-missing">IFSC missing — cannot include in NEFT</span>}
                          </>
                        ) : (
                          <span className="bank-missing">Bank details missing</span>
                        )}
                      </td>
                      <td className="number-cell">{Number(row.unpaidPoints || 0).toFixed(2)}</td>
                      <td>
                        <span className={`payout-badge ${payoutStatus.toLowerCase()}`}>{payoutStatus}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Level Configuration */}
        {levelConfig && (
          <div className="level-config-section">
            <h2>PV Distribution Configuration</h2>
            <div className="config-info">
              <span>PV Rate: <strong>{levelConfig.pvPercentageOfOrder}%</strong> of order value</span>
            </div>
            <div className="levels-grid">
              {levelConfig.levels?.map((level) => (
                <div key={level.levelId} className="level-chip">
                  <span className="level-num">L{level.levelId}</span>
                  <span className="level-pct">{level.pvPercentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="filters-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search by username, email, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-btn">Search</button>
          </form>
        </div>

        {/* Users Table */}
        <div className="users-table-container">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <>
              <table className="users-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('username')} className="sortable">
                      User {getSortIcon('username')}
                    </th>
                    <th onClick={() => handleSort('totalEarned')} className="sortable">
                      Total Earned {getSortIcon('totalEarned')}
                    </th>
                    <th onClick={() => handleSort('selfEarn')} className="sortable">
                      Self Earned {getSortIcon('selfEarn')}
                    </th>
                    <th onClick={() => handleSort('referralEarn')} className="sortable">
                      Referral Earned {getSortIcon('referralEarn')}
                    </th>
                    <th onClick={() => handleSort('balance')} className="sortable">
                      Balance {getSortIcon('balance')}
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId}>
                      <td>
                        <div className="user-cell">
                          <span className="user-name">{user.username}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </td>
                      <td className="number-cell">{user.balance?.totalEarned?.toFixed(2) || '0.00'}</td>
                      <td className="number-cell">{user.selfEarnings?.toFixed(2) || '0.00'}</td>
                      <td className="number-cell highlight">{user.referralEarnings?.toFixed(2) || '0.00'}</td>
                      <td className="number-cell balance">{user.balance?.currentBalance?.toFixed(2) || '0.00'}</td>
                      <td>
                        <button 
                          className="view-btn"
                          onClick={() => handleUserClick(user)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {pagination && pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span>Page {currentPage} of {pagination.totalPages}</span>
                  <button
                    disabled={currentPage === pagination.totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="detail-modal-overlay" onClick={closeDetail}>
            <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>User Points Details</h2>
                <button className="close-btn" onClick={closeDetail}>×</button>
              </div>

              {detailLoading ? (
                <div className="loading">Loading...</div>
              ) : userDetail && (
                <div className="modal-content">
                  <div className="user-info-section">
                    <div className="user-avatar">{userDetail.user?.username?.charAt(0)?.toUpperCase()}</div>
                    <div className="user-info">
                      <h3>{userDetail.user?.username}</h3>
                      <p>{userDetail.user?.email}</p>
                      {(userDetail.user?.firstName || userDetail.user?.lastName) && (
                        <p>{userDetail.user?.firstName} {userDetail.user?.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="detail-stats">
                    <div className="stat-item">
                      <span className="stat-label">Total Earned</span>
                      <span className="stat-value">{userDetail.balance?.totalPointsEarned?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Redeemed</span>
                      <span className="stat-value">{userDetail.balance?.totalPointsRedeemed?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Current Balance</span>
                      <span className="stat-value highlight">{userDetail.balance?.currentBalance?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  {userDetail.levelEarnings?.length > 0 && (
                    <div className="level-earnings-section">
                      <h4>Level-wise Earnings</h4>
                      <div className="level-earnings-grid">
                        {userDetail.levelEarnings.map((le) => (
                          <div key={le.levelId} className="level-earning-item">
                            <span className="le-level">Level {le.levelId}</span>
                            <span className="le-points">{le.totalPoints?.toFixed(2)} pts</span>
                            <span className="le-count">{le.transactionCount} txns</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {userDetail.recentTransactions?.length > 0 && (
                    <div className="transactions-section">
                      <h4>Recent Transactions</h4>
                      <div className="transactions-list">
                        {userDetail.recentTransactions.slice(0, 10).map((txn) => (
                          <div key={txn.transactionId} className="txn-row">
                            <div className="txn-type-badge">
                              {txn.transactionType === 'EARNED_SELF' ? '🛒' : '👥'}
                            </div>
                            <div className="txn-info">
                              <span className="txn-desc">{txn.description}</span>
                              <span className="txn-date">{formatDate(txn.createdAt)}</span>
                            </div>
                            <span className="txn-amount">+{txn.pointsAmount?.toFixed(2)}</span>
                            {(txn.transactionType === 'EARNED_SELF' || txn.transactionType === 'EARNED_REFERRAL') && (
                              <span className={`payout-badge ${(txn.paymentStatus || 'Pending').toLowerCase()}`}>
                                {txn.paymentStatus || 'Pending'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPointsReportPage;
